/* eslint-disable complexity */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box/box.component';
import Pill from 'carbon-react/lib/components/pill';

import { getTimestampRelativeFormat } from '@futrli/localisation';
import { ListingPageRowLayout } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-layout/listing-page-row-layout';
import { ListingPageRowMetricCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-metric-cell/listing-page-row-metric-cell';
import { ListingPageRowNameCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-name-cell/listing-page-row-name-cell';
import {
  ExtendedOrganisationListItem,
  ExtendedWorkspaceListItem
} from '@futrli/views/operations/workspaces/processors/workspacesList';
import { OrganisationDSDisplayStatus } from '@futrli/views/operations/workspaces/types';

import { getWorkspaceListingUrl } from '../../../../../utils/route-utils';
import { OrganisationRowActions } from '../organisation-row-actions/organisation-row-actions';
import { useOrganisationsActions } from '../../../../context/organisations-actions-context/organisations-actions-context';

import styles from './organisation-row.scss';

export const ROW_HEIGHT_DEFAULT = 78;

export interface WorkspaceOrganisationListItem {
  workspace: ExtendedWorkspaceListItem;
  organisation: ExtendedOrganisationListItem;
}

interface OrganisationRowProps {
  item: WorkspaceOrganisationListItem;
  hideWorkspaceColumn?: boolean;
}

const getStatusLabel = (status: OrganisationDSDisplayStatus | null, intl: ReturnType<typeof useIntl>) => {
  switch (status) {
    case OrganisationDSDisplayStatus.SYNCED:
      return intl.formatMessage({ id: 'common.synced', defaultMessage: 'Synced' });
    case OrganisationDSDisplayStatus.SYNCING:
      return intl.formatMessage({ id: 'common.syncing', defaultMessage: 'Syncing' });
    case OrganisationDSDisplayStatus.CONNECTING:
      return intl.formatMessage({ id: 'common.connecting', defaultMessage: 'Connecting' });
    case OrganisationDSDisplayStatus.PENDING_SETUP:
      return intl.formatMessage({ id: 'common.pending_setup', defaultMessage: 'Pending setup' });
    case OrganisationDSDisplayStatus.SYNC_ERROR:
      return intl.formatMessage({ id: 'common.sync_error', defaultMessage: 'Sync error' });
    case OrganisationDSDisplayStatus.NOT_CONNECTED:
      return intl.formatMessage({ id: 'common.not_connected', defaultMessage: 'Not connected' });
    default:
      return '-';
  }
};

const renderStatusPill = (status: OrganisationDSDisplayStatus | null, intl: ReturnType<typeof useIntl>) => {
  if (!status) {
    return '-';
  }

  const label = getStatusLabel(status, intl);

  if (status === OrganisationDSDisplayStatus.SYNCED) {
    return (
      <Pill colorVariant="positive" pillRole="status" size="S" fill>
        {label}
      </Pill>
    );
  }

  if (status === OrganisationDSDisplayStatus.SYNC_ERROR || status === OrganisationDSDisplayStatus.NOT_CONNECTED) {
    return (
      <Pill colorVariant="negative" pillRole="status" size="S" fill>
        {label}
      </Pill>
    );
  }

  return (
    <Pill size="S" pillRole="status" fill>
      {label}
    </Pill>
  );
};

export const OrganisationRow = ({ item, hideWorkspaceColumn = false }: OrganisationRowProps) => {
  const intl = useIntl();
  const { workspace, organisation } = item;

  const { handleOrganisationNameClick } = useOrganisationsActions();

  return (
    <ListingPageRowLayout
      nameColumn={
        <ListingPageRowNameCell
          name={
            <Box display="flex" alignItems="center">
              {organisation.integration && (
                <img
                  className={styles.icon}
                  src={organisation.integration?.image}
                  alt={organisation.integration?.name}
                />
              )}
              {organisation.name}
            </Box>
          }
          disabled={organisation.can_be_accessed === false}
          onNameClick={() => handleOrganisationNameClick(workspace, organisation)}
        />
      }
      metrics={
        <>
          {!hideWorkspaceColumn && (
            <ListingPageRowMetricCell
              label={<FormattedMessage id="predict.workspaces.workspace" defaultMessage="Workspace" />}
              value={
                <Link className={styles.workspaceLink} to={getWorkspaceListingUrl(workspace.id)}>
                  {workspace.name ||
                    intl.formatMessage(
                      { id: 'landing.default_workspace_name', defaultMessage: "{name}'s workspace" },
                      { name: workspace?.owner.first_name }
                    )}
                </Link>
              }
              size="status"
            />
          )}
          <ListingPageRowMetricCell
            label={<FormattedMessage id="predict.workspaces.datasource" defaultMessage="Datasource" />}
            value={organisation.integration?.name || '—'}
            size="status"
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.status" defaultMessage="Status" />}
            value={renderStatusPill(organisation.datasourceDisplayStatus, intl)}
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.last_synced" defaultMessage="Last synced" />}
            value={
              organisation.flags?.isExcelOrg
                ? '-'
                : organisation.datasource?.sync_status_modified
                ? getTimestampRelativeFormat(organisation.datasource.sync_status_modified, Date.now(), 1)
                : '-'
            }
          />
        </>
      }
      actions={<OrganisationRowActions workspace={workspace} organisation={organisation} />}
    />
  );
};
